import React from "react";
import { IBrandBarProps } from "./BrandBarProps";
import { LogoLink, StyledRow } from "./BrandBarHelpers";
import { graphql, useStaticQuery, Link } from "gatsby";
import IBrand from "../../../@types/IBrand";
import { useMobileDetectContext } from "../../../context/MobileDetectContext";
const BrandBar: React.FC<IBrandBarProps> = (props) => {
  const { isMobileOnly } = useMobileDetectContext();

  const FOOTER_BRANDS = [
    {
      to: "/royal-sonesta",
      logo: (
        <svg className="royal-sonesta" width={102} height={53}>
          <title>The Royal Sonesta</title>
          <use xlinkHref="/svg/newFooterLogos/the-royal-sonesta.svg#the-royal-sonesta"></use>
        </svg>
      ),
      position: 1,
    },

    {
      to: "/the-james",
      logo: (
        <svg className="james top-adjustment" width={106.2} height={37.8}>
          <title>The James Hotels</title>
          <use xlinkHref="/svg/newFooterLogos/james-hotel.svg#james-hotel"></use>
        </svg>
      ),
      position: 2,
    },
    {
      to: "/classico-sonesta-collection",
      logo: (
        <svg
          className="classico-sonesta-collection top-adjustment"
          width={138.2}
          height={23.5}
        >
          <title>Classico Sonesta Collection</title>
          <use xlinkHref="/svg/newFooterLogos/classico.svg#classico"></use>
        </svg>
      ),
      position: 3,
    },
    {
      to: "/sonesta-hotels-resorts",
      logo: (
        <svg
          className="sonesta-hotels-resorts top-adjustment"
          width={130.8}
          height={24.3}
        >
          <title>Sonesta Hotels Resorts</title>
          <use xlinkHref="/svg/newFooterLogos/sonesta-hotel-resorts.svg#sonesta-hotel-resorts"></use>
        </svg>
      ),
      position: 4,
    },
    {
      to: "/mod-sonesta-collection",
      logo: (
        <svg
          className="mod-sonesta-collection top-adjustment"
          width={74.1}
          height={23.8}
        >
          <title>Mod Sonesta Collection</title>
          <use xlinkHref="/svg/newFooterLogos/mod.svg#mod"></use>
        </svg>
      ),
      position: 6,
    },

    {
      to: "/sonesta-select",
      logo: (
        <svg className="sonesta-select" width={86.9} height={36.5}>
          <title>Sonesta Select</title>
          <use xlinkHref="/svg/newFooterLogos/sonesta-select.svg#sonesta-select"></use>
        </svg>
      ),
      position: 5,
    },
    {
      to: "/red-lion-hotels",
      logo: (
        <svg
          className="red-lion-hotels top-adjustment-minor"
          width={116.7}
          height={30.6}
        >
          <title>Red Lion Hotels</title>
          <use xlinkHref="/svg/newFooterLogos/redlion-hotels.svg#redlion-hotels"></use>
        </svg>
      ),
      position: 7,
    },
    {
      to: "/sonesta-es-suites",
      logo: (
        <svg
          className="sonesta-es-suites top-adjustment-mid"
          width={123.1}
          height={27.4}
        >
          <title>Sonesta Es Suites</title>
          <use xlinkHref="/svg/newFooterLogos/sonesta-es-suites.svg#sonesta-es-suites"></use>
        </svg>
      ),
      position: 8,
    },
    {
      to: "/sonesta-simply-suites",
      logo: (
        <svg
          className="sonesta-simply-suites top-adjustment-mid"
          width={137.4}
          height={31.6}
        >
          <title>Sonesta simply suites</title>
          <use xlinkHref="/svg/newFooterLogos/sonesta-simply-suites.svg#sonesta-simply-suites"></use>
        </svg>
      ),
      position: 9,
    },
    {
      to: "/sonesta-essential",
      logo: (
        <svg
          className="sonesta-essential top-adjustment-mid"
          width={130}
          height={30}
        >
          <title>Sonesta Essential</title>
          <use xlinkHref="/svg/newFooterLogos/sonesta-essential.svg#sonesta-essential"></use>
        </svg>
      ),
      position: 10,
    },
    {
      to: "/americas-best-value-inn",
      logo: (
        <svg
          className="americas-best-value-inn top-adjustment-mid"
          width={104.4}
          height={38.1}
        >
          <title>Americas Best Value Inn</title>
          <use xlinkHref="/svg/newFooterLogos/abvi.svg#abvi"></use>
        </svg>
      ),
      position: 11,
    },
    {
      to: "/canadas-best-value-inn",
      logo: (
        <svg
          className="canadas-best-value-inn top-adjustment-minor3"
          width={107}
          height={44.9}
        >
          <title>Canadas Best Value Inn</title>
          <use xlinkHref="/svg/newFooterLogos/cbvi.svg#cbvi"></use>
        </svg>
      ),
      position: 12,
    },
    {
      to: "/signature-inn",
      logo: (
        <svg
          className="signature-inn right-adjustment"
          width={89.9}
          height={51.5}
        >
          <title>Signature Inn</title>
          <use xlinkHref="/svg/newFooterLogos/signature-inn.svg#signature-inn"></use>
        </svg>
      ),
      position: 13,
    },
  ];

  const mobileLogos = [...FOOTER_BRANDS].sort(
    (a, b) => a.position - b.position
  );
  const mobileLogoPattern = [2, 3, 5, 6, 8, 9, 11, 12];

  const data = useStaticQuery(graphql`
    {
      allRlhsite(filter: { machine_name: { eq: "sonesta" } }) {
        edges {
          node {
            relationships {
              field_brands {
                id
                name
                field_featured_brands
                path {
                  alias
                }
                relationships {
                  field_logo {
                    publicUrl
                  }
                }
              }
            }
          }
        }
      }
    }
  `);
  const brands =
    data?.allRlhsite?.edges[0]?.node?.relationships?.field_brands || [];

  const brandEdit = (brand: IBrand, idx: any) => {
    const logo = brand.relationships.field_logo;
    const path = brand.path.alias;
    if (logo !== null && path !== null && !brand?.field_featured_brands) {
      return (
        <div
          key={`${brand.id}_LogoContainer`}
          style={{ width: "unset" }}
          id={"brand-" + idx}
        >
          <LogoLink
            id={"brand-link"}
            to={path}
            title={brand.name}
            aria-label={brand.name}
            className="text-center w-100"
          >
            <img
              id="brand-image"
              style={
                props.footer
                  ? isMobileOnly
                    ? { marginTop: "0px", maxWidth: "70px" }
                    : { marginTop: "0px" }
                  : {}
              }
              src={logo.publicUrl}
              alt={brand.name}
            />
          </LogoLink>
        </div>
      );
    }
  };

  return (
    <StyledRow footer={props.footer}>
      {!props.footer ? (
        <>
          <div className="brand-title" id="featured-brands">
            Featured Brands
          </div>
          {brands.map((brand: IBrand, idx: any) => {
            return brandEdit(brand, idx + 1);
          })}
        </>
      ) : (
        <div className="footerLogos">
          <div className="large-device">
            <div className={`footer-row`}>
              {FOOTER_BRANDS.map((brand, idx) => (
                <span
                  className="brand-container"
                  key={`footer-brand-${idx}`}
                  id={`footer-brand-${idx}`}
                >
                  <Link to={brand.to}>{brand.logo}</Link>
                </span>
              ))}
            </div>
          </div>
          <div className="medium-device">
            <div className={`footer-row`}>
              {mobileLogos.map((brand, idx) => (
                <span
                  className={`brand-container ${
                    mobileLogoPattern.includes(brand.position)
                      ? "half-logo"
                      : ""
                  }`}
                  key={`footer-brand-${idx}`}
                  id={`footer-brand-${idx}`}
                >
                  <Link to={brand.to}>{brand.logo}</Link>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </StyledRow>
  );
  // return (
  //   <div className="sonesta-container">
  //     <div className="sonesta-brand">
  //       <img src="/svg/newFooterLogos/sonesta-international.svg" alt="Sonesta International Hotels" />
  //     </div>
  //     <div className="hotel-brands">
  //       <img src="/svg/newFooterLogos/the-royal-sonesta.svg" alt="The Royal Sonesta" />
  //       <img src="/svg/newFooterLogos/james-hotel.svg" alt="The James Hotels" />
  //       <img src="/svg/newFooterLogos/classico.svg" alt="Classico" />
  //       <img src="/svg/newFooterLogos/sonesta-hotel-resorts.svg" alt="Sonesta Hotels, Resorts & Cruises" />
  //       <img src="/svg/newFooterLogos/mod.svg" alt="Sonesta MOD" />
  //       <img src="/svg/newFooterLogos/sonesta-select.svg" alt="Sonesta Select" />
  //       <img src="/svg/newFooterLogos/redlion-hotels.svg" alt="Red Lion Hotels, Inns & Suites" />
  //     </div>
  //     <div className="other-brands">
  //       <img src="/svg/newFooterLogos/sonesta-es-suites.svg" alt="Sonesta ES Suites" />
  //       <img src="/svg/newFooterLogos/sonesta-simply-suites.svg" alt="Sonesta Simply Suites" />
  //       <img src="/svg/newFooterLogos/sonesta-essential.svg" alt="Sonesta Essential" />
  //       <img src="/svg/newFooterLogos/abvi.svg" alt="America's Best Value Inn" />
  //       <img src="/svg/newFooterLogos/cbvi.svg" alt="Canada's Best Value Inn" />
  //       <img src="/svg/newFooterLogos/signature-inn.svg" alt="Signature" />
  //     </div>
  //   </div>
  // );
};

export default BrandBar;
